import {
  ALL_SHARD_MODULES,
  getTranslatedText,
  formatDataForSelectDropdown,
  formatCustomFieldForDropDown,
  COMMON_CONSTANTS,
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP
} from '@nile/components';
import {
  AccountClient,
  apiResolver,
  GET_CLIENTS,
  GET_FIELD,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  TicketClient
} from '@nile/graphql';
import { cloneDeep } from 'lodash';
import { GET_CLIENT_SITE_LIST_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/site';

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'prioritizedId':
      return 'displayId';
    default:
      return f.name;
  }
};

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === getRuleAttribute(f);
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
      case 'daterange':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

const checkDynamicQuery = (f) => {
  if (f.isCustom) return true;
  switch (f.name) {
    case 'client':
    case 'site':
    case 'subcategory':
    case 'subcause':
      return true;
    default:
      return false;
  }
};

const getDynamicQuery = (f) => {
  const resolverModule = 'Ticket';
  let client = TicketClient;
  let query = apiResolver[resolverModule][f.name]?.api;
  const variables = apiResolver[resolverModule][f.name]?.variables || {};

  if (f.isCustom) {
    return AccountClient.query({
      query: GET_FIELD,
      variables: {
        input: {
          columnName: f?.name || f?.columnName,
          entity: ALL_SHARD_MODULES.ticket
        }
      }
    });
  }
  switch (f.name) {
    case 'client':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    case 'site':
      return AccountClient.query({
        query: GET_CLIENT_SITE_LIST_FOR_DROPDOWN,
        variables: {
          input: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    default:
      query = apiResolver[resolverModule][f.name]?.api;
      client = apiResolver[resolverModule][f.name]?.client;
      return client.query({
        query: query,
        variables: variables
      });
  }
};

const getMethodName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'getAllCategory';
    case 'subcause':
      return 'getAllCause';
    default:
      return 'name';
  }
};

const getKeyName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'subCategories';
    case 'subcause':
      return 'subCauses';
    default:
      return 'name';
  }
};

const generateOptions = (f, data, params) => {
  let allValues = [];
  if (f.isCustom) {
    allValues = formatCustomFieldForDropDown(
      data?.getField?.options || data?.getField?.fieldOptions || []
    );
    return allValues;
  }
  switch (f.name) {
    case 'client':
      allValues = formatDataForSelectDropdown(data.getClientList.clients, {
        name: 'accountId'
      });
      break;
    case 'site':
      allValues = formatDataForSelectDropdown(
        data?.getClientSiteList?.sites?.filter((site) =>
          params?.accountId ? site.account.accountId === params.accountId : true
        ),
        { name: 'siteId' }
      );
      break;
    case 'subcategory':
    case 'subcause':
      data?.getField?.options?.forEach((el) => {
        allValues.push({
          name: el.value,
          value: el.value,
          label: el.value
        });
      });
      break;
    default:
      allValues = [];
  }
  return allValues;
};

function getQuery(property) {
  switch (property.name) {
    case 'technician':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

function getNameKey(property) {
  switch (property.name) {
    case 'technician':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

const fetchValues = async (client, query, params, otherOperands) => {
  const attribute = 'name';
  const nameKey = getNameKey(params.property);
  let filterOperands = [
    {
      attribute: attribute || 'name',
      operator: 'startsWith',
      value: params.inputValue
    }
  ];
  if (otherOperands?.[nameKey]) {
    filterOperands = [...otherOperands[nameKey], ...filterOperands];
  }
  const listInfo = {
    condition: {
      joinOperator: 'AND',
      operands: filterOperands
    }
  };

  let variables = { listInfo };

  if (COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]) {
    const input = { listInfo };

    if (nameKey === 'getTechnicianList') {
      input.includeAgents = [{ module: 'TICKET' }];
    }

    variables = { input };
  }

  const res = await client.query({
    query: query,
    variables
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];

  return allOptions;
};

const getAsyncOptions = (property) => {
  return { client: AccountClient, query: getQuery(property) };
};

const getReferredEntityData = (data) => {
  const { meta, entityName, attributeName, intl } = data;
  const filteredEntity = meta.find((entity) => {
    return entity.name === entityName;
  });
  const filteredAttribute = filteredEntity?.attributes.find((el) => {
    return el.name === 'name';
  });
  return {
    ...filteredAttribute,
    name: attributeName,
    label: `${getTranslatedText(
      intl,
      filteredAttribute.attributePlaceHolder.label
    )}`
  };
};

const getAllowedProperties = (meta, intl) => {
  const ticketMeta = meta.find((el) => el.name === 'Ticket');
  let allowedProperties = [];
  ticketMeta.attributes.forEach((el) => {
    switch (el.name) {
      case 'resolutionTimer':
      case 'approvalStatus':
      case 'statusChangeTime':
      case 'resolutionCode':
      case 'sla':
      case 'operationalStatus':
      case 'uri':
      case 'firstResponseStartTime':
      case 'sop':
      case 'subcategory':
      case 'approverPermission':
      case 'firstResponseEscalationTime':
      case 'readStatus':
      case 'activeType':
      case 'mergedToTicket':
      case 'resolutionStartTime':
      case 'splitFrom':
      case 'firstResponseViolated':
      case 'resolutionTimespent':
      case 'currentApproval':
      case 'assets':
      case 'firstResponseResumeTime':
      case 'totalTasks':
      case 'firstResponseTimespent':
      case 'resolutionPauseTime':
      case 'ticketType':
      case 'currentReply':
      case 'approvals':
      case 'inactiveTime':
      case 'completedTasks':
      case 'firstResponseTimer':
      case 'resolutionViolated':
      case 'tasks':
      case 'ticketId':
      case 'resolutionEscalationTime':
      case 'additionalRequester':
      case 'taskSummary':
      case 'dormantType':
      case 'createdBy':
      case 'resolutionResumeTime':
      case 'currentNote':
      case 'followerPermission':
      case 'prioritizedId':
      case 'worklogTimespent':
        break;
      case 'client':
      case 'site':
      case 'updatedTime':
      case 'subject':
      case 'impact':
      case 'techGroup':
      case 'source':
      case 'requester':
      case 'technician':
      case 'firstResponseDueTime':
      case 'urgency':
      case 'resolutionTime':
      case 'displayId':
      case 'cause':
      case 'priority':
      case 'subcause':
      case 'firstResponseTime':
      case 'followers':
      case 'resolutionDueTime':
      case 'status':
      case 'category':
      case 'createdTime':
      default:
        if (el.label) {
          allowedProperties.push({
            ...el,
            label: getTranslatedText(intl, el.label)
          });
        }
    }
  });
  allowedProperties = allowedProperties.sort((a, b) =>
    a.label.toUpperCase() > b.label.toUpperCase()
      ? 1
      : b.label.toUpperCase() > a.label.toUpperCase()
      ? -1
      : 0
  );
  return allowedProperties;
};

const getVariables = (property) => {
  switch (property.name) {
    case 'client':
      return { listInfo: {} };
    default:
      return null;
  }
};

const getDateOperators = (intl) => {
  const dateOperators = [
    {
      key: 'placeholder.today',
      label: getTranslatedText(intl, 'placeholder.today'),
      value: " placeholder.today',"
    },
    {
      key: 'placeholder.tomorrow',
      label: getTranslatedText(intl, 'placeholder.tomorrow'),
      value: " placeholder.tomorrow',"
    },
    {
      key: 'placeholder.yesterday',
      label: getTranslatedText(intl, 'placeholder.yesterday'),
      value: " placeholder.yesterday',"
    },
    {
      key: 'placeholder.last.week',
      label: getTranslatedText(intl, 'placeholder.last.week'),
      value: 'placeholder.last.week'
    },
    {
      key: 'placeholder.this.week',
      label: getTranslatedText(intl, 'placeholder.this.week'),
      value: 'placeholder.this.week'
    },
    {
      key: 'placeholder.next.week',
      label: getTranslatedText(intl, 'placeholder.next.week'),
      value: 'placeholder.next.week'
    },
    {
      key: 'placeholder.last.month',
      label: getTranslatedText(intl, 'placeholder.last.month'),
      value: 'placeholder.last.month'
    },
    {
      key: 'placeholder.this.month',
      label: getTranslatedText(intl, 'placeholder.this.month'),
      value: 'placeholder.this.month'
    },
    {
      key: 'placeholder.next.month',
      label: getTranslatedText(intl, 'placeholder.next.month'),
      value: 'placeholder.next.month'
    }
  ];
  return dateOperators;
};

const getDynamicKey = (property) => {};

export {
  getDefaultValue,
  getDynamicQuery,
  checkDynamicQuery,
  generateOptions,
  getMethodName,
  getKeyName,
  getVariables,
  getDateOperators,
  fetchValues,
  getAsyncOptions,
  getAllowedProperties
};
